import React from 'react';
import PropTypes from 'prop-types';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import Modal from '../modal';
import Utils from '../utils';
import Communication from '../utils/Communication';
import MultiSelectModal from './MultiSelectModal';
import CleanseAddressStatus from './CleanseAddressStatus.enum';
import ResultCode from './ResultCode.enum';

const { ModalContext, ModalButtonActionType } = Modal;
const { Util, Logger } = Utils;

class CleanseAddress extends React.Component {
	componentDidMount = () => {
		const { fullAddress } = this.props;

		this.cleanseAddress(fullAddress);
	};

	/**
	 * Build params to call AVS validateAddress api
	 * @param {object} address
	 * @returns {object}
	 */
	buildValidateAddressParams = (address = {}) => ({
		address_line1: address.addressLine1,
		address_line2: address.addressLine2,
		address_line3: address.addressLine3,
		city: address.city,
		company_name: address.companyName,
		country_code: address.countryCode,
		email: address.email,
		formatted_address: address.formattedAddress,
		name: address.name,
		phone: address.phone,
		postal_code: address.postalCode,
		residential_indicator: address.residentialIndicator,
		state_province: address.stateProvince,
	});

	/**
	 * Get address cleanse status
	 * @param {object} validatedAddress
	 * - {ResultCode} resultCode - provide overall status of the success of the address verification.
	 * - {array} candidateAddresses - an array of suggested addresses if the provided address is ambiguous.
	 * @param {boolean} isSuggestedAddress - true if the "validatedAddress" is based on an address from "candidate_addresses".
	 * @returns {CleanseAddressStatus}
	 */
	getCleanseStatus = (validatedAddress, isSuggestedAddress) => {
		const { validationResults = {}, candidateAddresses = [] } = validatedAddress;
		const { resultCode } = validationResults;

		switch (resultCode) {
			// full address verified
			case ResultCode.V100:
				return CleanseAddressStatus.Cleansed;

			// multiple match
			case ResultCode.V200:
				return candidateAddresses.length > 0
					? CleanseAddressStatus.MultipleMatch
					: CleanseAddressStatus.NotFound;
			case ResultCode.P100:
				return candidateAddresses.length > 0
					? CleanseAddressStatus.MultipleMatch
					: CleanseAddressStatus.MalformedAddress;

			// partial match
			case ResultCode.P200:
				return isSuggestedAddress && candidateAddresses.length === 0
					? CleanseAddressStatus.InvalidCandidateAddress
					: CleanseAddressStatus.MalformedAddress;
			case ResultCode.P300:
			case ResultCode.P400:
			case ResultCode.P500:
			case ResultCode.P600:
				return CleanseAddressStatus.MalformedAddress;

			// error
			case ResultCode.E100:
			default:
				return CleanseAddressStatus.NotFound;
		}
	};

	/**
	 * @param {string} address
	 * @returns {object}
	 */
	parseFreeFormAddress = async address => {
		const { showError } = this.context;
		const response = await Communication.parseAddress({
			full_address: address,
		});

		if (!Communication.isSuccessful(response)) {
			showError({
				response,
			});
		}

		return response.data || {};
	};

	/**
	 * @param {object|string} fullAddress an address to be validated
	 * @param {boolean} isSuggestedAddress true if "fullAddress" is from "candidate_address" passed from MultiSelectModal
	 */
	cleanseAddress = async (fullAddress, isSuggestedAddress = false) => {
		const { callback, validateRecipient } = this.props;
		const { showModal, showError } = this.context;

		// if no address to cleanse, just call back status and return
		if (isEmpty(fullAddress) || (isString(fullAddress) && fullAddress.trim() === '')) {
			callback({
				cleanseStatus: CleanseAddressStatus.EmptyAddress,
				cleanseMessage: 'Address is required.',
			});
			return;
		}

		const parsedAddress = isString(fullAddress)
			? await this.parseFreeFormAddress(fullAddress) // a free form address will need to be parsed first
			: this.buildValidateAddressParams(fullAddress);

		// NOTE: validation takes an array of address objects to validate.
		const response = await Communication.validateAddress([parsedAddress]);

		if (!Communication.isSuccessful(response)) {
			callback({
				cleanseStatus: CleanseAddressStatus.ServerFailure,
				cleanseMessage:
					'Invalid delivery address. Check for errors in the address or fieldName and try again.',
			});

			showError({
				response,
			});
			return;
		}

		const validatedAddress = isArray(response.data) ? Util.toCamelCase(response.data[0]) : {};
		const cleanseStatus = this.getCleanseStatus(validatedAddress, isSuggestedAddress);
		const {
			validationResults = {},
			candidateAddresses = [],
			extendedInfo = {},
			matchedAddress,
			isPoBox,
		} = validatedAddress;

		switch (cleanseStatus) {
			case CleanseAddressStatus.Cleansed:
				if (validateRecipient === true) {
					callback({
						cleanseStatus:
							!matchedAddress.name && !matchedAddress.companyName
								? CleanseAddressStatus.InvalidRecipient
								: cleanseStatus,
						cleanseMessage:
							!matchedAddress.name && !matchedAddress.companyName
								? 'A First Name and Last Name or Company are required.'
								: '',
						address: matchedAddress,
						isPoBox,
					});
				} else {
					callback({
						cleanseStatus,
						cleanseMessage: '',
						address: matchedAddress,
						isPoBox,
					});
				}
				break;
			case CleanseAddressStatus.MultipleMatch:
				showModal({
					component: MultiSelectModal,
					data: {
						matchedAddress,
						candidateAddresses,
						callback: this.multiSelectCallback,
					},
				});
				break;
			case CleanseAddressStatus.InvalidCandidateAddress:
				/**
				 * WEBAPPS-31593, log when the address user selected from "candidate_addresses" is validated as invalid.
				 * This "InvalidCandidateAddress" cleanse status is determined based on the following conditions:
				 *
				 * (1) isSuggestedAddress : true if the address is from "candidate_addresses", which user would select from "MultiSelectModal".
				 * (2) "result_code" = P200
				 * (3) "candidate_addresses" length is 0
				 */
				Logger.logToServer({
					category: 'cleanse_address_error',
					result_code: validationResults.resultCode,
					extended_info: extendedInfo,
				});

				callback({
					cleanseStatus: CleanseAddressStatus.InvalidCandidateAddress,
					cleanseMessage:
						'The selected address does not meet return delivery requirements. Please choose a different address.',
				});
				break;
			case CleanseAddressStatus.MalformedAddress:
				callback({
					cleanseStatus: CleanseAddressStatus.MalformedAddress,
					cleanseMessage:
						"Invalid Address: We couldn't find an exact match for the city, state, and ZIP Code.",
				});
				break;
			default:
				callback({
					cleanseStatus: CleanseAddressStatus.NotFound,
				});
		}
	};

	multiSelectCallback = (modalId, selectedAddress) => {
		const { callback } = this.props;
		const { closeModal } = this.context;
		const isSuggestedAddress = true;

		// make a second cleanse address call with the selected address so cleanse hash can be returned
		if (selectedAddress) {
			this.cleanseAddress(selectedAddress, isSuggestedAddress);
		} else {
			// return the cleanse status when the user doesn't select any suggested address
			callback({
				cleanseStatus: CleanseAddressStatus.MultipleMatch,
				cleanseMessage:
					"Ambiguous Address: We couldn't find your exact address.  Only the city, state, and ZIP Code fields have been validated.",
			});
		}

		closeModal(modalId, ModalButtonActionType.Ok);
	};

	render() {
		return null;
	}
}

CleanseAddress.contextType = ModalContext;

CleanseAddress.propType = {
	// string type - free form address will be parsed first then use the payload to validate.
	// object type - the address object will be validated directly.
	fullAddress: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

	// `true` to validate the recipient fields (name and companyName).
	// For example, ShipFromAddress component does not require recipient validation.
	validateRecipient: PropTypes.bool,

	// return the cleanse address status back to the caller
	callback: PropTypes.func,
};
export default CleanseAddress;
